// /owner/logs — merged activity log (stamps + reward redemptions). Range
// dropdown (Today .. All time), activity-type chips (All / Single stamp /
// Multiple stamps / Rewards claimed), a name/card-code search box, an
// optional branch picker (only shown when the tenant has ≥2 branches), and
// a "Load more" cursor-paginated list.
//
// A multi-stamp action (see the stamp overlay's +/- stepper) collapses into
// ONE row here — "5 stamps · Juan · 2 -> 7 / 10" — instead of 5 separate
// identical-timestamp rows. Reward redemptions show who claimed what and
// which staff/owner approved it.
//
// The TanStack hook applies a 30s staleTime so quick filter flicks don't
// re-hit the API.

import { useEffect, useState } from 'react'

import { useBranches } from '../hooks/useBranches.js'
import {
  useStampHistory,
  type ActivityTypeFilter,
  type HistoryRange,
} from '../hooks/useStampHistory.js'
import {
  CustomerDetailModal,
  type CustomerDetailRow,
} from '../components/CustomerDetailModal.js'
import { LogRow } from '../components/ActivityLogRow.js'
import './logsPage.css'

const RANGE_LABELS: Record<HistoryRange, string> = {
  today: 'Today',
  '3d': 'Last 3 days',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '60d': 'Last 2 months',
  '90d': 'Last 3 months',
  all: 'All time',
}

const TYPE_LABELS: Record<ActivityTypeFilter, string> = {
  all: 'All activity',
  single_stamp: 'Single stamp',
  multi_stamp: 'Multiple stamps',
  redemption: 'Rewards claimed',
  adjustment: 'Corrections',
}

export function LogsPage() {
  const [range, setRange] = useState<HistoryRange>('today')
  const [type, setType] = useState<ActivityTypeFilter>('all')
  const [branchId, setBranchId] = useState<string | undefined>(undefined)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  // Customer lifecycle (2026-09-05) — click a row to open the same detail
  // drawer the Customers page uses, so a stamp correction or revoke/erase is
  // one tap away from the log entry that prompted it.
  const [detailFor, setDetailFor] = useState<CustomerDetailRow | null>(null)

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query.trim()), 300)
    return () => window.clearTimeout(handle)
  }, [query])

  const branches = useBranches()
  const branchList = branches.data?.branches ?? []
  const showBranchPicker = branchList.length >= 2

  const history = useStampHistory(range, branchId, type, debouncedQuery)
  const items = history.data?.pages.flatMap((p) => p.items) ?? []

  return (
    <div>
      <h1 className="owner-greeting">Logs</h1>
      <p className="owner-greeting-sub">Recent stamp + reward activity.</p>

      <div className="logs-controls">
        <div className="logs-chips" role="group" aria-label="Time range">
          {(Object.keys(RANGE_LABELS) as HistoryRange[]).map((r) => (
            <button
              key={r}
              type="button"
              className={`logs-chip${range === r ? ' is-active' : ''}`}
              onClick={() => setRange(r)}
              aria-pressed={range === r}
            >
              {RANGE_LABELS[r]}
            </button>
          ))}
        </div>

        {showBranchPicker && (
          <select
            className="owner-input logs-branch-select"
            value={branchId ?? ''}
            onChange={(e) => setBranchId(e.target.value || undefined)}
            aria-label="Filter by branch"
          >
            <option value="">All branches</option>
            {branchList.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="logs-controls">
        <div className="logs-chips" role="group" aria-label="Activity type">
          {(Object.keys(TYPE_LABELS) as ActivityTypeFilter[]).map((t) => (
            <button
              key={t}
              type="button"
              className={`logs-chip${type === t ? ' is-active' : ''}`}
              onClick={() => setType(t)}
              aria-pressed={type === t}
            >
              {TYPE_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      <input
        type="text"
        className="owner-input logs-search"
        placeholder="Search by customer name or card code"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoComplete="off"
        spellCheck={false}
        aria-label="Search activity by customer name or card code"
      />

      {history.isLoading && (
        <p className="owner-loading">Loading activity…</p>
      )}

      {history.isError && (
        <p className="owner-field-error" role="alert">
          Couldn't load the activity log. Try refreshing.
        </p>
      )}

      {history.isSuccess && items.length === 0 && (
        <div className="logs-empty">
          <p>
            {debouncedQuery
              ? 'No activity matches that search.'
              : 'No activity in this range yet.'}
          </p>
        </div>
      )}

      {history.isSuccess && items.length > 0 && (
        <ol className="logs-list" aria-label="Activity log">
          {items.map((item) => (
            <LogRow
              key={item.id}
              item={item}
              onOpen={() =>
                setDetailFor({
                  card_id: item.card_id,
                  card_code: item.card_code,
                  first_name: item.customer_first_name,
                })
              }
            />
          ))}
        </ol>
      )}

      {history.hasNextPage && (
        <button
          type="button"
          className="owner-btn owner-btn-block logs-load-more"
          onClick={() => void history.fetchNextPage()}
          disabled={history.isFetchingNextPage}
        >
          {history.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </button>
      )}

      {detailFor && (
        <CustomerDetailModal row={detailFor} onClose={() => setDetailFor(null)} />
      )}
    </div>
  )
}
